import PropTypes from 'prop-types';
import { useCallback, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';

import Card from '@/components/card/card-component';
import { Col, Row } from '@/components/grid/grid-component';
import { Heading3Text } from '@/utils/global-fonts';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';

import getCompanyPriceListSelect from '@/api/get/config/get-company-price-list-select';
import BreadcrumbPortal from '@/components/breadcrumb/breadcrumb-portal';
import {
  PrimaryButton,
  PrimaryOutlineButton,
} from '@/components/button/button-component';
import Loader from '@/components/loader/loader-component';
import { CloseIcon } from '@/components/modal/modal-style';
import PermissionWrapper from '@/components/permission-wrapper/permission-wrapper-component';
import { useLazyQuery } from '@/hooks/use-api';
import OptionFormFields from './option-form-fields';
import OptionsVariantComponent from './option-variant/options-variant';

const FooterCol = styled(Col)`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 10px;
  background-color: #eaedf3 !important;
  width: 100%;
  padding: 20px;
`;

/**
 * Option form
 * @param {object} props - The props passed to the OptionForm component.
 * @param {string} props.type - form type edit or create
 * @param {boolean} props.isLoading - Loading state
 * @param {Function} props.submitForm - Form submission handler
 * @param {boolean} props.modal - Whether the form is used in modal mode
 * @param {Function} props.closeModal - Function to close the modal
 * @returns {JSX.Element}
 */
const OptionsForm = ({
  type,
  isLoading: updateLoading,
  submitForm,
  modal,
  closeModal,
}) => {
  const navigate = useNavigate();
  const { id: companyId } = useParams();

  const getPricelist = useCallback(
    () => getCompanyPriceListSelect(companyId),
    [companyId],
  );

  const {
    data: pricelistData,
    loading: pricelistLoading,
    refetch,
  } = useLazyQuery(getPricelist);

  const {
    formState: { isLoading: formLoading },
    handleSubmit,
    getValues,
  } = useFormContext();

  const isLoading = formLoading || updateLoading;

  // Create default variant data from existing variant prices
  const createVariantDefaults = useMemo(
    () => () => {
      const optionData = getValues('option');
      const pricesData = getValues('prices');

      if (!optionData || !pricesData?.length) return {};

      // Use the selected price or first price
      const selectedPrice =
        pricesData.find(
          (price) => price.price_id === optionData.selectedPriceId,
        ) || pricesData[0];

      return {
        variant: {
          name: optionData.name || '',
          description: optionData.description || '',
          quote_description: optionData.quote_description || '',
          brochure_link: optionData.brochure_link || '',
          video_link: optionData.video_link || '',
          status: optionData.status || 'active',
          selectedPriceId: selectedPrice?.price_id || null,
          thumbnail: optionData.thumbnail || null,
          media: optionData.media ? [...optionData.media] : null,
        },
      };
    },
    [getValues],
  );

  if (isLoading) {
    return <Loader loading $height={modal ? '400px' : '100%'} />;
  }

  return (
    <Row justify="center" $margin="0 !important">
      <Col sm={12} md={12} lg={12} xl={12} xxl={12}>
        {!modal && (
          <PermissionWrapper
            permissions={['option.create', 'option.update']}
            satisfyAll={false}
          >
            <BreadcrumbPortal>
              <PrimaryOutlineButton
                buttonWidth="108px"
                type="Button"
                loading={false}
                title="Cancel"
                onClick={() => {
                  navigate(-1);
                }}
                margin="0 0 0 0"
              />
              <PrimaryButton
                buttonWidth="108px"
                type="submit"
                loading={isLoading}
                title="Save"
                onClick={() => {
                  handleSubmit(submitForm)();
                }}
              />
            </BreadcrumbPortal>
          </PermissionWrapper>
        )}
      </Col>
      <Card padding="16px 18px 10px 16px" border={modal ? 'none' : null}>
        {!modal && (
          <Row justify="center">
            <Col xs={12}>
              <Heading3Text>
                {type === 'create' ? 'Add Option' : 'Edit Option'}
              </Heading3Text>
            </Col>
          </Row>
        )}

        <OptionFormFields
          fieldPrefix="option"
          isLoading={isLoading}
          pricelistData={pricelistData}
          pricelistLoading={pricelistLoading}
          refetchPriceList={refetch}
          nested={modal}
        />
      </Card>

      <Card margin="25px 0 0 0" padding="0px">
        <OptionsVariantComponent
          type={type}
          name="variant"
          createVariantDefaults={createVariantDefaults}
          originalOptions={getValues('option')}
        />
      </Card>

      {modal && (
        <FooterCol xs={12}>
          <PermissionWrapper
            permissions={['option.create', 'option.update']}
            satisfyAll={false}
          >
            <PrimaryOutlineButton
              icon={<CloseIcon />}
              buttonWidth="110px"
              title="Cancel"
              type="button"
              onClick={closeModal}
            />
            <PrimaryButton
              title="Update"
              buttonWidth="110px"
              type="button"
              onClick={() => {
                handleSubmit(submitForm)();
              }}
              isLoading={isLoading}
            />
          </PermissionWrapper>
        </FooterCol>
      )}
    </Row>
  );
};

OptionsForm.defaultProps = {
  isLoading: false,
  modal: false,
  closeModal: null,
};

OptionsForm.propTypes = {
  type: PropTypes.string.isRequired,
  isLoading: PropTypes.bool,
  submitForm: PropTypes.func.isRequired,
  modal: PropTypes.bool,
  closeModal: PropTypes.func,
};

export default OptionsForm;
